import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Info, FileSpreadsheet, FileText, Activity, TrendingUp, BarChart3, PieChart as PieChartIcon, Sparkles } from "lucide-react";
import { ConsultantPerformanceReport } from "@/components/ConsultantPerformanceReport";
import { ReportAiSummaryContent } from "@/components/ReportAiSummaryContent";
import { VoiceAutomationReport } from "@/components/VoiceAutomationReport";
import { DailyBookingsPanel } from "@/components/DailyBookingsPanel";
import { CallReconciliation } from "@/components/CallReconciliation";
import { ReportDateFilter } from "@/components/ReportDateFilter";
import { ReportScopeBanner } from "@/components/ReportScopeBanner";
import { BookingActivityPanel } from "@/components/BookingActivityPanel";
import { ConsultationLifecyclePanel } from "@/components/ConsultationLifecyclePanel";
import { EmployeeResultsPanel } from "@/components/EmployeeResultsPanel";
import { FinanceLedgerPanel } from "@/components/FinanceLedgerPanel";
import { isFamilyRoot, productQuery, summaryStaleness } from "@/lib/reportScope";
import { useAuth } from "@/context/AuthContext";
import { useUrlState } from "@/hooks/useUrlState";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";

// Human-readable column headers for the drill-down table — falls back to
// the raw key (title-cased) for anything not listed here.
const DRILLDOWN_COLUMN_LABELS = {
  customer: "Customer", customer_id: "Customer ID", product: "Product",
  event_at_ist: "Event time (IST)", performer: "Performer", outcome: "Outcome",
  consultant: "Consultant", booked_by: "Booked by", booking_origin: "Booking source",
  status: "Status", reason: "Reason", opportunity_id: "Opportunity",
  recorded_outcome: "Recorded outcome", outcome_at: "Outcome time (UTC)", bucket_label: "Bucket",
  booked_product_code: "Booked as", resolved_product_code: "Product at outcome", product_evidence_label: "Product evidence",
  current_opportunity_product_code_NOT_EVIDENCE: "Today's product (not evidence)", opportunity_lifecycle: "Pursuit state",
  case_id: "Case", commercially_completed: "Commercially completed",
};
const DRILLDOWN_HIDDEN_COLUMNS = new Set([
  "interaction_id", "appointment_id", "audit_id", "performer_id", "consultant_id", "bucket", "product_evidence", "is_test_record",
]);

const backend = process.env.REACT_APP_BACKEND_URL;

// R16 N3: the trend's buckets are IST calendar days. The tooltip shows the day exactly as the server bucketed it (never a browser-timezone re-parse) and says when the window covers only part of it.
export const trendTooltipLabel = (label, payload) => {
  const row = payload && payload[0] && payload[0].payload;
  if (!row || !row.date) return label;
  return `${row.date} (IST)${row.partial ? " - part of the day" : ""}`;
};

// "Consultation Booking Automation" manages its own data entirely (same
// precedent as Consultant Performance) — org-level Leadership only
// (reports.company | reports.team, same gate the backend enforces), never
// shown to a reports.own-only employee (raw Voice transcripts live here).
// 2026-09 Phase 2 (F1) — relabeled "Calling": human calling performance
// only (AI Voice excluded backend-side, routers_platform.py's "sales"
// branch). The internal key stays "sales" deliberately — renaming it would
// silently orphan existing db.ai_report_summaries history (keyed by
// section="sales") and the reports-tab-sales test hook for zero user-
// visible benefit; only the label changes.
const SECTIONS_BASE = [
  { key: "sales", label: "Calling" },
  { key: "consultations", label: "Consultations" },
  { key: "revenue", label: "Revenue" },
  { key: "cashflow", label: "Cashflow" },
  { key: "process", label: "Process / Visa" },
  { key: "performance", label: "Employee Performance" },
  { key: "consultant_performance", label: "Consultant Performance" },
  { key: "voice_automation", label: "Consultation Booking Automation", orgOnly: true },
  { key: "support", label: "Support" },
];
const SELF_CONTAINED_SECTIONS = ["consultant_performance", "voice_automation"];

const PIE_COLORS = ["hsl(var(--primary))", "#7c6af0", "#2e9e77", "#e0a83c", "#d9534f", "#4aa3df"];

const ChartTypeToggle = ({ value, onChange, options }) => (
  <div className="flex items-center gap-1">
    {options.map(([v, Icon, label]) => (
      <Button key={v} type="button" size="sm" variant={value === v ? "default" : "outline"}
        className="h-7 px-2" title={label} data-testid={`chart-type-${v}`} onClick={() => onChange(v)}>
        <Icon className="h-3.5 w-3.5" />
      </Button>
    ))}
  </div>
);

export default function ReportsPage() {
  const navigate = useNavigate();
  const { hasCap } = useAuth();
  const SECTIONS = SECTIONS_BASE.filter((s) => !s.orgOnly || hasCap("reports.company") || hasCap("reports.team"));
  // 2026-09 P5A — every filter (section, date range, product, and the new
  // Caller/Consultant/Booking-source attribution dimensions) lives in URL
  // state, so a report view survives navigation and browser Back/Forward,
  // not just in-memory state.
  const [urlState, setUrlState] = useUrlState({
    section: "sales", preset: "30", days: "30", date_from: "", date_to: "",
    product: "all", scope: "family", caller: "all", consultant: "all", booking_source: "all", owner: "all",
  });
  const { section, product, caller, consultant, booking_source: bookingSource, owner } = urlState;
  const setSection = (v) => setUrlState({ section: v });
  const setProduct = (v) => setUrlState({ product: v, scope: "family" });
  const dateFilter = { preset: urlState.preset, days: urlState.days,
    date_from: urlState.date_from || null, date_to: urlState.date_to || null };
  const setDateFilter = (v) => setUrlState({ preset: v.preset, days: v.days || "30",
    date_from: v.date_from || "", date_to: v.date_to || "" });
  const dateParams = useCallback(() => dateFilter.date_from || dateFilter.date_to
    ? `date_from=${encodeURIComponent(dateFilter.date_from || "")}&date_to=${encodeURIComponent(dateFilter.date_to || "")}`
    : `days=${dateFilter.days || 30}`, [dateFilter.date_from, dateFilter.date_to, dateFilter.days]);
  const attributionParams = useCallback(() => {
    const p = new URLSearchParams();
    if (caller && caller !== "all") p.set("caller", caller);
    if (consultant && consultant !== "all") p.set("consultant", consultant);
    if (bookingSource && bookingSource !== "all") p.set("booking_source", bookingSource);
    // Customer Owner — deliberately a SEPARATE dimension, only meaningful
    // for the customer-portfolio cards (Lost/Won/Pipeline), never merged
    // with Caller/Consultant/Booker.
    if (owner && owner !== "all") p.set("owner", owner);
    return p.toString();
  }, [caller, consultant, bookingSource, owner]);
  const [products, setProducts] = useState([]);
  const [productsReady, setProductsReady] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [trendChartType, setTrendChartType] = useState("area");
  const [distChartType, setDistChartType] = useState("bar");
  const [aiSummary, setAiSummary] = useState(null);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // {metric, label}
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [perfPage, setPerfPage] = useState(1);
  // R15 R6: a report that FAILED to load is never shown as an endless skeleton or as zeros. 403 = not allowed (retrying will not help); anything else = retryable.
  const [loadError, setLoadError] = useState(null);

  // R16 N3: request identity for the generic section / trend path. Every load takes a number; a response (or error) from anything but the LATEST load - a previous date, product, section or
  // attribution filter that answered late - is dropped, so an old scope can never overwrite the one on screen. (The Daily bookings panel keeps its own generation + abort handling.)
  const loadSeq = useRef(0);
  const perfSeq = useRef(0);
  useEffect(() => { api.get("/products").then((r) => { setProducts(r.data); setProductsReady(true); }).catch(() => setProductsReady(true)); }, []);
  // R17: the product scope (whole family vs exact code) goes to EVERY request - cards, trend, drilldown, export, AI summary and the new panels - so nothing can answer for a different scope.
  const productQ = productQuery(products, product, urlState.scope);
  const productExtra = productQ ? `&${productQ}` : "";
  const rootChosen = isFamilyRoot(products, product);
  const attrQ = attributionParams();          // R17 review F2: the SAME caller / consultant / booking-source / owner selection the cards use goes to the reconciled panels too
  // R17: with a product chosen, the scope (family vs exact) depends on the product list; with All products nothing does - so "ready" never re-fires a load for All products
  const scopeReady = product === "all" || productsReady;
  useEffect(() => { api.get("/users").then((r) => setEmployees(r.data.users.filter((u) => u.is_active))).catch(() => {}); }, []);
  const load = useCallback(() => {
    // Consultant Performance is a genuinely different shape (period-type
    // selector, per-consultant snapshots from consultant_score_snapshots
    // via /ai/consultants) — it manages its own data entirely, never the
    // generic days/product-filtered /reports/{section} endpoint every
    // other tab shares.
    const seq = ++loadSeq.current;
    if (SELF_CONTAINED_SECTIONS.includes(section)) { setData(null); setTrend([]); return; }
    if (!scopeReady) return;       // the scope depends on the product list (is it a family root?) - never ask before it is known
    setData(null);
    setTrend([]);
    setLoadError(null);
    setPerfPage(1);
    const attrQ = attributionParams();
    const extra = `${productExtra}${attrQ ? `&${attrQ}` : ""}`;
    api.get(`/reports/${section}?${dateParams()}${extra}`)
      .then((r) => { if (seq === loadSeq.current) setData(r.data); })
      .catch((e) => {
        if (seq !== loadSeq.current) return;
        const status = e?.response?.status;
        setLoadError({ kind: status === 403 ? "forbidden" : "retryable", status: status || null, message: errDetail(e, "Could not load this report") });
      });
    api.get(`/reports/trend?${dateParams()}${productExtra}`)
      .then((r) => { if (seq === loadSeq.current) setTrend(r.data); }).catch(() => {});
  }, [section, dateParams, productExtra, scopeReady, attributionParams]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (section !== "performance" || SELF_CONTAINED_SECTIONS.includes(section)) return;
    const attrQ = attributionParams();
    const extra = `${productExtra}${attrQ ? `&${attrQ}` : ""}`;
    const mySeq = ++perfSeq.current;
    const loadAtStart = loadSeq.current;
    api.get(`/reports/${section}?${dateParams()}${extra}&pperf_page=${perfPage}&pperf_limit=50`)
      .then((r) => { if (mySeq === perfSeq.current && loadAtStart === loadSeq.current) setData(r.data); })
      .catch((e) => { if (mySeq === perfSeq.current && loadAtStart === loadSeq.current) toast.error(errDetail(e, "Could not load that page of results")); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfPage]);

  const openDrilldown = (metric, label) => {
    setDrilldown({ metric, label });
    setDrilldownPage(1);
  };
  useEffect(() => {
    if (!drilldown) { setDrilldownData(null); return; }
    const attrQ = attributionParams();
    const extra = `${productExtra}${attrQ ? `&${attrQ}` : ""}`;
    api.get(`/reports/${section}/drilldown?metric=${drilldown.metric}&${dateParams()}${extra}&page=${drilldownPage}&limit=25`)
      .then((r) => setDrilldownData(r.data)).catch((e) => { toast.error(errDetail(e)); setDrilldown(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drilldown, drilldownPage]);
  const drilldownExportUrl = () => {
    const attrQ = attributionParams();
    const extra = `${productExtra}${attrQ ? `&${attrQ}` : ""}`;
    return `${backend}/api/reports/${section}/export?fmt=xlsx&metric=${drilldown.metric}&${dateParams()}${extra}`;
  };

  // 2026-09 P5B.1 — the AI-summary generate/history calls now carry the
  // EXACT SAME filters the visible report itself uses (product, date range,
  // and every attribution filter this section shows), so a previously
  // generated summary only ever reappears when it genuinely matches what's
  // currently on screen — never a company-wide summary surfacing under a
  // filtered employee report, or vice versa. `pperf_page`/`pperf_limit`
  // (Employee Performance's own employee-scope selector) are included too.
  const aiSummaryParams = useCallback(() => {
    const params = new URLSearchParams({ section });
    if (dateFilter.date_from || dateFilter.date_to) {
      params.set("date_from", dateFilter.date_from || ""); params.set("date_to", dateFilter.date_to || "");
    } else {
      params.set("days", dateFilter.days || 30);
    }
    if (productQ) for (const [k, v] of new URLSearchParams(productQ)) params.set(k, v);       // R17: product AND scope - a summary is bound to the scope it describes
    const attrQ = attributionParams();
    if (attrQ) for (const [k, v] of new URLSearchParams(attrQ)) params.set(k, v);
    if (section === "performance") {
      params.set("pperf_page", perfPage); params.set("pperf_limit", 50);
    }
    return params;
  }, [section, dateFilter.date_from, dateFilter.date_to, dateFilter.days, productQ, attributionParams, perfPage]);

  // Reload whenever the section OR any filter that affects this section's
  // evidence changes — a summary generated under different filters must
  // never keep showing once the report itself has moved on.
  const aiSeq = useRef(0);
  useEffect(() => {
    const mine = ++aiSeq.current;
    if (SELF_CONTAINED_SECTIONS.includes(section)) { setAiHistory([]); setAiSummary(null); return; }
    if (!scopeReady) return;          // the scope (family vs exact) is unknown until the product list has loaded - never ask with a guess
    api.get(`/ai/reports-summary/history?${aiSummaryParams().toString()}`).then((r) => {
      if (mine !== aiSeq.current) return;                      // an older scope that answered late never replaces the one on screen
      const items = r.data.items || [];
      setAiHistory(items);
      setAiSummary(items[0] || null);
    }).catch(() => { if (mine === aiSeq.current) { setAiHistory([]); setAiSummary(null); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, dateFilter.date_from, dateFilter.date_to, dateFilter.days, productQ, scopeReady,
      caller, consultant, bookingSource, owner, perfPage]);

  const generateAiSummary = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/ai/reports-summary?${aiSummaryParams().toString()}`);
      setAiSummary(data);
      setAiHistory((prev) => [data, ...prev].slice(0, 20));
    } catch (e) { toast.error(errDetail(e, "AI unavailable — report continues normally")); }
    finally { setAiBusy(false); }
  };

  // 2026-09 P5A correction — the whole-report export must represent the
  // SAME filtered population the visible cards/tables show, never just
  // date range + product.
  const exportUrl = (fmt) => {
    const attrQ = attributionParams();
    const extra = `${productExtra}${attrQ ? `&${attrQ}` : ""}`;
    return `${backend}/api/reports/${section}/export?fmt=${fmt}&${dateParams()}${extra}`;
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground">Every KPI declares its counting grain, date basis and definition — hover the info icon. TEST records excluded.</p>
        </div>
        {!SELF_CONTAINED_SECTIONS.includes(section) && (
          <div className="ml-auto flex gap-2 flex-wrap">
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger data-testid="reports-product-filter" className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {rootChosen && (
              <div className="flex items-center rounded border overflow-hidden h-9" role="group" aria-label="Product scope" data-testid="reports-scope-toggle">
                <button type="button" data-testid="reports-scope-family" aria-pressed={urlState.scope !== "exact"} onClick={() => setUrlState({ scope: "family" })}
                  className={`px-2 text-xs ${urlState.scope !== "exact" ? "bg-primary text-primary-foreground" : "bg-background"}`}>Whole family</button>
                <button type="button" data-testid="reports-scope-exact" aria-pressed={urlState.scope === "exact"} onClick={() => setUrlState({ scope: "exact" })}
                  className={`px-2 text-xs border-l ${urlState.scope === "exact" ? "bg-primary text-primary-foreground" : "bg-background"}`}>This code only</button>
              </div>
            )}
            {/* 2026-09 P5A — Caller (real call performer) is meaningful only
                for Calling; Consultant (assigned) and Booking source only
                for Calling/Consultations booking-attributed cards. Never
                conflated with each other or with Customer Owner. */}
            {section === "sales" && (
              <Select value={caller} onValueChange={(v) => setUrlState({ caller: v })}>
                <SelectTrigger data-testid="reports-caller-filter" className="h-9 w-40 text-xs"><SelectValue placeholder="Caller" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All callers</SelectItem>
                  {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {(section === "sales" || section === "consultations") && (
              <Select value={consultant} onValueChange={(v) => setUrlState({ consultant: v })}>
                <SelectTrigger data-testid="reports-consultant-filter" className="h-9 w-40 text-xs"><SelectValue placeholder="Consultant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All consultants</SelectItem>
                  {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {(section === "sales" || section === "consultations") && (
              <Select value={bookingSource} onValueChange={(v) => setUrlState({ booking_source: v })}>
                <SelectTrigger data-testid="reports-booking-source-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Booking source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All booking sources</SelectItem>
                  <SelectItem value="employee">Employee booked</SelectItem>
                  <SelectItem value="ai_direct">AI booked (direct)</SelectItem>
                  <SelectItem value="ai_assisted">AI-assisted</SelectItem>
                  <SelectItem value="self_booking">Customer self-booked</SelectItem>
                  <SelectItem value="leadership_admin">Admin/Leadership booked</SelectItem>
                  <SelectItem value="unknown_legacy">Legacy/unattributed</SelectItem>
                </SelectContent>
              </Select>
            )}
            {section === "sales" && (
              <Select value={owner} onValueChange={(v) => setUrlState({ owner: v })}>
                <SelectTrigger data-testid="reports-owner-filter" className="h-9 w-44 text-xs"><SelectValue placeholder="Customer owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customer owners</SelectItem>
                  {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <ReportDateFilter preset={dateFilter.preset} onChange={setDateFilter} testIdPrefix="reports-date" />
            <a href={exportUrl("xlsx")} target="_blank" rel="noreferrer">
              <Button variant="outline" className="h-9" data-testid="reports-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />XLSX</Button>
            </a>
            <a href={exportUrl("pdf")} target="_blank" rel="noreferrer">
              <Button variant="outline" className="h-9" data-testid="reports-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
            </a>
          </div>
        )}
      </div>
      <Tabs value={section} onValueChange={setSection}>
        <TabsList className="flex-wrap h-auto">
          {SECTIONS.map((s) => <TabsTrigger key={s.key} data-testid={`reports-tab-${s.key}`} value={s.key}>{s.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {section === "consultant_performance" ? (
        <ConsultantPerformanceReport reportsRange={{ date_from: dateFilter.date_from, date_to: dateFilter.date_to, days: dateFilter.days, product, scope: urlState.scope, productQ }} />
      ) : section === "voice_automation" ? (
        <VoiceAutomationReport />
      ) : (
      <>
      {!data && loadError ? (
        <div data-testid={loadError.kind === "forbidden" ? "reports-forbidden" : "reports-load-error"} role="alert"
          className="rounded border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-4 text-sm space-y-2">
          {loadError.kind === "forbidden" ? (
            <p><strong>You do not have access to this report.</strong> {loadError.message} Ask an administrator if you need it — trying again will not change this.</p>
          ) : (
            <>
              <p><strong>This report could not be loaded{loadError.status ? ` (error ${loadError.status})` : ""}.</strong> {loadError.message} Nothing is shown rather than showing zeros that look like real results.</p>
              <Button data-testid="reports-retry" size="sm" variant="outline" onClick={() => load()}>Try again</Button>
            </>
          )}
        </div>
      ) : !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <TooltipProvider>
          <ReportScopeBanner scope={data.scope} />
          {data.note && <p className="text-sm text-muted-foreground" data-testid="reports-note">{data.note}</p>}
          {data.coverage && data.coverage.complete === false && (
            <p role="alert" className="text-sm rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2" data-testid="reports-coverage-incomplete">
              <b>Incomplete data.</b> {data.coverage.statement}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.cards || []).map((c) => (
              <Card key={c.key} data-testid={`kpi-${c.key}`}
                className={(c.drill_metric || c.drill) ? "cursor-pointer hover:border-primary/50" : ""}
                onClick={() => c.drill_metric ? openDrilldown(c.drill_metric, c.label)
                                              : c.drill && navigate(c.drill)}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    <Tooltip>
                      <TooltipTrigger asChild><Info className="h-3 w-3 text-muted-foreground shrink-0" /></TooltipTrigger>
                      <TooltipContent className="max-w-56 text-xs">
                        <p><b>Grain:</b> {c.grain}</p>
                        <p><b>Date basis:</b> {c.date_basis}</p>
                        <p>{c.definition}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="font-heading text-2xl font-semibold mt-1" data-testid={c.lower_bound ? `kpi-${c.key}-lower-bound` : undefined}
                     title={c.coverage_note || undefined}>{c.lower_bound ? `at least ${c.value}` : c.value}</p>
                  {c.lower_bound && (
                    <p className="text-[10px] text-amber-700 mt-0.5">
                      {c.true_total != null ? `true total ${c.true_total}` : "true total unknown"} — source truncated
                    </p>
                  )}
                  {(c.drill_metric || c.drill) && <p className="text-[10px] text-muted-foreground mt-0.5">Click to drill down</p>}
                </CardContent>
              </Card>
            ))}
          </div>
          {(section !== "performance" || hasCap("reports.performance.view")) && (() => {
            const stale = summaryStaleness(aiSummary, { rolling: !(dateFilter.date_from || dateFilter.date_to) });
            const body = aiSummary ? (
              <div className="space-y-1">
                <ReportAiSummaryContent summary={aiSummary} />
                <p className="text-[10px] text-muted-foreground">Generated {fmtIST(aiSummary.created_at)} · {aiSummary.model} · AI can be wrong — verify against the figures above</p>
                {aiSummary.freshness && (aiSummary.freshness.is_stale || aiSummary.freshness.reasons?.length > 0) && (
                  <div data-testid="ai-summary-freshness" className={`text-[11px] rounded border px-2 py-1 ${aiSummary.freshness.is_stale ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10" : "bg-muted/40"}`}>
                    <p className="font-medium">{aiSummary.freshness.is_stale ? "Out of date — this summary describes older data" : aiSummary.freshness.is_stale === null ? "Freshness unknown" : "Check the date"}</p>
                    <p>{aiSummary.freshness.note}</p>
                    {aiSummary.freshness.stale_metrics?.slice(0, 5).map((m) => (
                      <p key={m.metric_key} className="font-mono">{m.label}: cited {m.cited}, now {m.current}</p>
                    ))}
                    <p className="text-muted-foreground">{aiSummary.freshness.regeneration}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Get a plain-language read on what this report's numbers mean — synthesized from the exact figures above, nothing else.</p>
            );
            return (
              <Card data-testid="reports-ai-panel">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" /> AI summary
                    <Badge variant="outline" className="text-[10px]">Advisory only</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {aiSummary && stale.collapsed ? (
                    <>
                      <p role="status" data-testid="ai-summary-stale-notice" className="text-xs rounded border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 px-2 py-1.5">
                        <b>Not current.</b> This summary was written {fmtIST(aiSummary.created_at)}; {stale.reasons.join("; ")}. The figures above are the current ones. It is kept for reference and is never rewritten — generate a new one if you need it.
                      </p>
                      <details data-testid="ai-summary-collapsed"><summary className="text-xs cursor-pointer">Show the saved summary</summary><div className="pt-2">{body}</div></details>
                    </>
                  ) : body}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button data-testid="reports-ai-generate" size="sm" variant="outline" disabled={aiBusy} onClick={generateAiSummary}>
                      {aiBusy ? "Generating…" : aiSummary ? "Generate a new summary" : "Get AI summary"}
                    </Button>
                    {aiHistory.length > 1 && (
                      <Select value={aiSummary?.ai_output_id || ""} onValueChange={(id) => setAiSummary(aiHistory.find((h) => h.ai_output_id === id) || null)}>
                        <SelectTrigger data-testid="reports-ai-history" className="h-8 w-56 text-xs"><SelectValue placeholder="Past generations" /></SelectTrigger>
                        <SelectContent>
                          {aiHistory.map((h) => (
                            <SelectItem key={h.ai_output_id} value={h.ai_output_id}>{fmtIST(h.created_at)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          {section === "sales" && <CallReconciliation reconciliation={data.call_reconciliation} />}
          {section === "sales" && <EmployeeResultsPanel qs={`${dateParams()}${productExtra}${attrQ ? `&${attrQ}` : ""}`} />}
          {(section === "revenue" || section === "cashflow") && <FinanceLedgerPanel qs={`${dateParams()}${productExtra}`} />}
          {trend.length > 0 && (
            <Card data-testid="reports-trend-chart"><CardContent className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Daily trend — leads · consultations · connected calls</p>
                <ChartTypeToggle value={trendChartType} onChange={setTrendChartType}
                  options={[["area", Activity, "Area"], ["line", TrendingUp, "Line"], ["bar", BarChart3, "Bar"]]} />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                {trendChartType === "bar" ? (
                  <BarChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip labelFormatter={trendTooltipLabel} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="leads" fill="hsl(var(--primary))" />
                    <Bar dataKey="consultations" fill="#7c6af0" />
                    <Bar dataKey="connected" fill="#2e9e77" />
                  </BarChart>
                ) : trendChartType === "line" ? (
                  <LineChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip labelFormatter={trendTooltipLabel} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="leads" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="consultations" stroke="#7c6af0" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="connected" stroke="#2e9e77" strokeWidth={2} dot={false} />
                  </LineChart>
                ) : (
                  <AreaChart data={trend} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip labelFormatter={trendTooltipLabel} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="leads" stroke="hsl(var(--primary))" fill="url(#gLeads)" strokeWidth={2} />
                    <Area type="monotone" dataKey="consultations" stroke="#7c6af0" fill="transparent" strokeWidth={2} />
                    <Area type="monotone" dataKey="connected" stroke="#2e9e77" fill="transparent" strokeWidth={2} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </CardContent></Card>
          )}
          {data.by_product && (
            <Card data-testid="reports-distribution-chart"><CardContent className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Revenue by product</p>
                <ChartTypeToggle value={distChartType} onChange={setDistChartType}
                  options={[["bar", BarChart3, "Bar"], ["pie", PieChartIcon, "Pie"]]} />
              </div>
              {data.by_product.length === 0 ? (
                <p className="text-xs text-muted-foreground">No committed customers in period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  {distChartType === "pie" ? (
                    <PieChart>
                      <Pie data={data.by_product.map((r) => ({ ...r, revenue: Number(r.revenue) }))}
                        dataKey="revenue" nameKey="product_code" cx="50%" cy="50%" outerRadius={80} label>
                        {data.by_product.map((r, i) => <Cell key={r.product_code} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  ) : (
                    <BarChart data={data.by_product.map((r) => ({ ...r, revenue: Number(r.revenue) }))}
                      margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="product_code" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="revenue" fill="hsl(var(--primary))" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              )}
            </CardContent></Card>
          )}
          {section === "cashflow" && data.cards?.length > 0 && (
            <Card data-testid="reports-cashflow-chart"><CardContent className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Inflow vs outflow</p>
                <ChartTypeToggle value={distChartType} onChange={setDistChartType}
                  options={[["bar", BarChart3, "Bar"], ["pie", PieChartIcon, "Pie"]]} />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                {distChartType === "pie" ? (
                  <PieChart>
                    <Pie data={[
                      { name: "Inflow", value: Number(data.cards.find((c) => c.key === "total_inflow")?.value || 0) },
                      { name: "Outflow", value: Number(data.cards.find((c) => c.key === "total_outflow")?.value || 0) },
                    ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      <Cell fill="#2e9e77" /><Cell fill="#d9534f" />
                    </Pie>
                    <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                ) : (
                  <BarChart data={[
                    { name: "Inflow", value: Number(data.cards.find((c) => c.key === "total_inflow")?.value || 0) },
                    { name: "Outflow", value: Number(data.cards.find((c) => c.key === "total_outflow")?.value || 0) },
                  ]} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="value">
                      <Cell fill="#2e9e77" /><Cell fill="#d9534f" />
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </CardContent></Card>
          )}
          {data.call_performance && <CallReconciliation reconciliation={data.call_reconciliation} />}
          {data.call_performance && (
            <div className="grid md:grid-cols-2 gap-3">
              <Card><CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Calls by employee</p>
                  <span className="text-[10px] text-muted-foreground">{data.call_performance.total} total</span>
                </div>
                {data.call_performance.items.length === 0 && <p className="text-xs text-muted-foreground">No call activity in period.</p>}
                {data.call_performance.items.map((r) => (
                  <div key={r.actor_id || "unattributed"} className="flex justify-between text-sm py-0.5"
                    data-testid={r.user === "Actor unavailable" ? "reports-actor-unavailable-row" : undefined}>
                    <span>{r.user}</span><span className="font-mono text-xs">{r.connected}/{r.calls} connected</span>
                  </div>
                ))}
                {data.call_performance.total > data.call_performance.limit && (
                  <div className="flex items-center justify-between pt-2 mt-2 border-t">
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={perfPage <= 1}
                      onClick={() => setPerfPage((p) => Math.max(1, p - 1))}>Previous</Button>
                    <span className="text-[10px] text-muted-foreground">Page {perfPage}</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!data.call_performance.has_more}
                      onClick={() => setPerfPage((p) => p + 1)}>Next</Button>
                  </div>
                )}
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Consultations by consultant (scheduled workload)</p>
                  <span className="text-[10px] text-muted-foreground">{data.consultant_workload.total} total</span>
                </div>
                {data.consultant_workload.items.length === 0 && <p className="text-xs text-muted-foreground">No scheduled consultations in period.</p>}
                {data.consultant_workload.items.map((r) => (
                  <div key={r.consultant_id || "unattributed"} className="flex justify-between text-sm py-0.5">
                    <span>{r.counsellor}</span><span className="font-mono text-xs">{r.count}</span>
                  </div>
                ))}
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <p className="text-sm font-semibold mb-2">Marked Lost by employee</p>
                {(!data.marked_lost_performance || data.marked_lost_performance.length === 0) && (
                  <p className="text-xs text-muted-foreground">No opportunities marked Lost in period.</p>
                )}
                {data.marked_lost_performance?.map((r) => (
                  <div key={r.actor_id || "unattributed"} className="flex justify-between text-sm py-0.5">
                    <span>{r.user}</span><span className="font-mono text-xs">{r.count}</span>
                  </div>
                ))}
              </CardContent></Card>
            </div>
          )}
          {section === "consultations" && <BookingActivityPanel qs={`${dateParams()}${productExtra}${attrQ ? `&${attrQ}` : ""}`} />}
          {section === "consultations" && <ConsultationLifecyclePanel qs={`${dateParams()}${productExtra}${attrQ ? `&${attrQ}` : ""}`} />}
          {section === "consultations" && (
            <div data-testid="single-day-override">
              <p className="text-xs text-muted-foreground pb-1">Single day — a deliberate one-day view with its own date and family picker; it does NOT follow the date range or product scope above.</p>
              <DailyBookingsPanel products={products} />
            </div>
          )}
          {data.consultation_outcome_buckets && (
            <Card data-testid="reports-outcome-buckets"><CardContent className="pt-4">
              <p className="text-sm font-semibold mb-1">Consultation outcomes — recorded intention vs agreement vs completion</p>
              <p className="text-xs text-muted-foreground mb-2">
                {data.consultation_outcome_buckets.recorded} recorded Signup / Agreed to Proceed outcome(s) split into the four rows below
                ({data.consultation_outcome_buckets.reconciles ? "they add up" : "DO NOT add up — report this"}).
                Commercial completion is counted separately and is not derived from them. Paid cash is a different measure again.
                {data.consultation_outcome_buckets.truncated && ` INCOMPLETE: only the newest ${data.consultation_outcome_buckets.examined} of ${data.consultation_outcome_buckets.total_matching} matching appointments were read, so these counts are at-least figures.`}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {["qualifying_agreement", "outcome_not_valid_for_product", "product_not_configured", "unresolved_product"].map((k) => (
                  <div key={k} data-testid={`outcome-bucket-${k}`} className="flex justify-between text-sm border rounded px-2 py-1" title={data.consultation_outcome_buckets.definitions[k]}>
                    <span>{data.consultation_outcome_buckets.bucket_labels[k]}</span>
                    <span className="font-mono text-xs">{data.consultation_outcome_buckets[k]}</span>
                  </div>
                ))}
                <div data-testid="outcome-bucket-commercial" className="flex justify-between text-sm border rounded px-2 py-1 bg-muted/40" title={data.consultation_outcome_buckets.definitions.commercial_completion}>
                  <span>Commercial completion (separate measure)</span>
                  <span className="font-mono text-xs">{data.consultation_outcome_buckets.commercial_completion}</span>
                </div>
              </div>
            </CardContent></Card>
          )}
          {data.commercial_conversion_by_product && (
            <Card data-testid="reports-commercial-conversion-breakdown"><CardContent className="pt-4">
              <p className="text-sm font-semibold mb-2">Commercial conversion by product</p>
              <p className="text-xs text-muted-foreground mb-2">
                A product with no commercial-conversion mechanism configured shows "Not configured" —
                never a silent zero that could be misread as "measured, none found."
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data.commercial_conversion_by_product.map((r) => (
                  <div key={r.product_code} className="flex justify-between text-sm border rounded px-2 py-1">
                    <span>{r.product_code}</span>
                    <span className="font-mono text-xs">{r.configured ? r.count : "Not configured"}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </TooltipProvider>
      )}
      </>
      )}

      <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reports-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{drilldown?.label}</DialogTitle>
          </DialogHeader>
          {!drilldownData ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : drilldownData.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No records for this metric with the current filters.</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground" data-testid="reports-drilldown-total">
                {drilldownData.truncated
                  ? `At least ${drilldownData.total} records (the report's source was truncated; true total ${drilldownData.true_total != null ? drilldownData.true_total : "unknown"}) — showing ${drilldownData.items.length}`
                  : `${drilldownData.total} record${drilldownData.total === 1 ? "" : "s"} — showing ${drilldownData.items.length}`}
              </p>
              {drilldownData.truncated && (
                <p role="alert" className="text-xs text-amber-700" data-testid="reports-drilldown-truncated">
                  Truncated source: this list and its export hold only the newest {drilldownData.examined} of {drilldownData.total_matching} matching appointments. Narrow the date range or filters for an exact, complete list.
                </p>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.keys(drilldownData.items[0])
                        .filter((k) => !DRILLDOWN_HIDDEN_COLUMNS.has(k))
                        .map((k) => <TableHead key={k}>{DRILLDOWN_COLUMN_LABELS[k] || k}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drilldownData.items.map((row, i) => (
                      <TableRow key={i}>
                        {Object.keys(row).filter((k) => !DRILLDOWN_HIDDEN_COLUMNS.has(k)).map((k) => (
                          <TableCell key={k} className="text-xs">
                            {k === "event_at_ist" && row[k] ? fmtIST(row[k]) : (typeof row[k] === "boolean" ? (row[k] ? "Yes" : "No") : (row[k] ?? "—"))}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between w-full">
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" disabled={drilldownPage <= 1}
                data-testid="reports-drilldown-prev" onClick={() => setDrilldownPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button size="sm" variant="outline" disabled={!drilldownData?.has_more}
                data-testid="reports-drilldown-next" onClick={() => setDrilldownPage((p) => p + 1)}>Next</Button>
            </div>
            {drilldown && (
              <a href={drilldownExportUrl()} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="reports-drilldown-export">
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />Export these rows
                </Button>
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
